<template>
<div class="mcp-doc">

    <!-- Quick facts -->
    <div class="mcp-facts">
        <div class="fact">
            <span class="fact__label">Connector URL</span>
            <code class="fact__value">https://mcp.interfy.io/mcp</code>
        </div>
        <div class="fact">
            <span class="fact__label">Authentication</span>
            <span class="fact__value">OAuth 2.1 — sign in with your account</span>
        </div>
        <div class="fact">
            <span class="fact__label">Works with</span>
            <span class="fact__value">Claude, ChatGPT connectors &amp; other MCP clients</span>
        </div>
    </div>

    <!-- Capabilities -->
    <section class="block">
        <h2>What you can do</h2>
        <p class="lead">The connector exposes a curated slice of the platform across three areas:</p>

        <div class="cap-grid">
            <div class="cap-card">
                <h3><span class="tag tag--bpm">BPM</span> Business Process Management</h3>
                <ul>
                    <li>List your open, available, and completed tasks</li>
                    <li>Claim a task, fill its form data, and complete it to advance the workflow</li>
                    <li>Start a new case from a process template</li>
                    <li>Reassign, cancel, or reopen tasks (for supervisors)</li>
                    <li>Read case notes, case progress, and process metrics</li>
                </ul>
            </div>
            <div class="cap-card">
                <h3><span class="tag tag--ecm">ECM</span> Enterprise Content Management</h3>
                <ul>
                    <li>Search documents and browse folders</li>
                    <li>Read document details, versions, and comments</li>
                    <li>Create folders and documents, move documents, add comments</li>
                    <li>Share a document with a user (optional expiration) and revoke shares</li>
                </ul>
            </div>
            <div class="cap-card">
                <h3><span class="tag tag--dss">DSS</span> Digital Signature Service</h3>
                <ul>
                    <li>List and inspect signature requests, incl. per-signer status</li>
                    <li>Create a new signature request for real signers</li>
                    <li>Cancel a pending signature request</li>
                </ul>
            </div>
        </div>

        <p class="callout">
            <strong>Tip:</strong> ask the assistant to run “who am I” first — it confirms your
            identity and the workspace you're connected to before you do anything else.
        </p>
    </section>

    <!-- Getting connected -->
    <section class="block">
        <h2>Getting connected</h2>

        <h3 class="sub">Claude.ai / ChatGPT <span class="pill">recommended</span></h3>
        <ol class="steps">
            <li>Add a custom connector pointing at <code>https://mcp.interfy.io/mcp</code>.</li>
            <li>When prompted, you'll be redirected to sign in (and pick your workspace if you belong to more than one).</li>
            <li>Approve the permissions you want to grant the assistant (see Permissions below).</li>
            <li>That's it — the assistant can now act in your workspace.</li>
        </ol>

        <h3 class="sub">Claude Code</h3>
        <p>Run:</p>
        <pre><code>claude mcp add --transport http interfy https://mcp.interfy.io/mcp</code></pre>
        <p class="muted">Claude Code walks you through the same sign-in and consent flow. Your workspace
        is determined automatically from your sign-in — you don't need to enter it manually.</p>
    </section>

    <!-- Permissions -->
    <section class="block">
        <h2>Permissions</h2>
        <p class="lead">
            When you connect, you grant one or more scopes. Request read-only access, or add
            write access per area — and revoke access at any time.
        </p>
        <div class="table-wrap">
            <table class="mcp-table">
                <thead>
                    <tr><th>Permission</th><th>What it allows</th></tr>
                </thead>
                <tbody>
                    <tr><td><code>mcp:read</code></td><td>Read tasks, cases, documents, folders, signatures, and workspace users</td></tr>
                    <tr><td><code>mcp:bpm:write</code></td><td>Claim / complete / start / reassign tasks and cases, add case notes</td></tr>
                    <tr><td><code>mcp:ecm:write</code></td><td>Create / move / share documents and folders, add comments</td></tr>
                    <tr><td><code>mcp:dss:write</code></td><td>Create and cancel signature requests</td></tr>
                </tbody>
            </table>
        </div>
        <p class="muted">
            Read and write tools are separate and clearly labeled, so the assistant knows which
            actions are safe and which change data before it acts. Some actions have real effects —
            e.g. creating a signature request emails real signers — and the assistant flags when an
            action is not easily undone.
        </p>
    </section>

    <!-- Security -->
    <section class="block">
        <h2>Security &amp; privacy</h2>
        <ul class="checks">
            <li>Sign in with OAuth 2.1 (PKCE) — no long-lived password or API key is shared with the AI client.</li>
            <li>Passthrough by design: the connector never stores your credentials or content. Access is used to answer each request, then discarded — no connector-side database or session store.</li>
            <li>Your workspace stays isolated — every request runs strictly within your own authenticated workspace.</li>
            <li>No conversation data is collected. The connector only sees the specific request the assistant makes.</li>
            <li>Revoke anytime by disconnecting the connector or revoking the token — access stops immediately.</li>
        </ul>
        <p class="muted">See the <a href="https://interfy.ai/legal/privacy">Privacy Policy</a> for full details.</p>
    </section>

    <!-- Support -->
    <section class="block block--last">
        <h2>Support</h2>
        <p>Need help connecting or have a question about the connector?</p>
        <div class="support-links">
            <a href="/support" class="support-links__btn">Contact support</a>
            <a href="https://interfy.ai/legal/privacy" class="support-links__link">Privacy policy</a>
        </div>
    </section>

</div>
</template>

<script>
export default {
  props: {
    company: {
      type: Object,
      default () {
        return { company_name: 'Interfy', company_fullname: 'Interfy' }
      }
    }
  }
}
</script>

<style scoped lang="scss">
$blue: #214cdb;
$ink: #1f2937;
$muted: #5b6472;
$line: #e6e9ef;

.mcp-doc {
  color: $ink;
  font-size: 16px;
  line-height: 1.65;

  code {
    font-family: "SFMono-Regular", Menlo, Consolas, monospace;
    font-size: .9em;
    color: $blue;
    background: #eef2ff;
    padding: 2px 7px;
    border-radius: 5px;
  }

  h2 {
    font-size: 28px;
    font-weight: 700;
    color: $ink;
    margin: 0 0 6px;
  }

  .block { padding: 40px 0; border-top: 1px solid $line; }
  .block:first-of-type { border-top: 0; }
  .block--last { padding-bottom: 8px; }

  .lead { color: $muted; margin-bottom: 24px; }
  .muted { color: $muted; font-size: 15px; }

  /* Quick facts */
  .mcp-facts {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 8px;

    .fact {
      background: #f7f9fc;
      border: 1px solid $line;
      border-radius: 12px;
      padding: 18px 20px;

      &__label {
        display: block;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: .09em;
        color: #8a93a3;
        margin-bottom: 8px;
      }
      &__value {
        font-size: 15px;
        color: $ink;
        background: transparent;
        padding: 0;
        word-break: break-word;
      }
      code.fact__value { color: $blue; }
    }
  }

  /* Capabilities cards */
  .cap-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    margin-bottom: 24px;
  }
  .cap-card {
    border: 1px solid $line;
    border-radius: 14px;
    padding: 22px 22px 8px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, .04);
    transition: box-shadow .2s ease, transform .2s ease;

    &:hover { box-shadow: 0 8px 24px rgba(16, 24, 40, .08); transform: translateY(-2px); }

    h3 {
      font-size: 15px;
      font-weight: 700;
      line-height: 1.35;
      margin-bottom: 14px;
    }
    ul { padding-left: 0; list-style: none; margin: 0; }
    li {
      position: relative;
      padding-left: 20px;
      margin-bottom: 10px;
      font-size: 14.5px;
      color: #414b5a;
    }
    li::before {
      content: "";
      position: absolute;
      left: 2px; top: 9px;
      width: 6px; height: 6px;
      border-radius: 50%;
      background: $blue;
    }
  }

  .tag {
    display: inline-block;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: .04em;
    color: #fff;
    padding: 2px 8px;
    border-radius: 6px;
    margin-right: 8px;
    vertical-align: middle;
  }
  .tag--bpm { background: #2f5bff; }
  .tag--ecm { background: #0ea5a3; }
  .tag--dss { background: #7c3aed; }

  /* Connect */
  h3.sub { font-size: 18px; font-weight: 700; margin: 26px 0 12px; }
  .pill {
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em;
    color: #0a7d3c; background: #e6f6ec; padding: 3px 9px; border-radius: 999px; margin-left: 6px;
    vertical-align: middle;
  }
  ol.steps { padding-left: 20px; }
  ol.steps li { margin-bottom: 8px; }

  pre {
    background: #0f172a;
    color: #e5e7eb;
    padding: 16px 18px;
    border-radius: 10px;
    overflow-x: auto;
    margin: 6px 0 14px;
    code { color: #e5e7eb; background: transparent; padding: 0; font-size: 14px; }
  }

  .callout {
    background: #eef4ff;
    border: 1px solid #d7e3ff;
    border-left: 4px solid $blue;
    color: #23324d;
    padding: 14px 18px;
    border-radius: 10px;
    font-size: 15px;
    margin: 4px 0 0;
  }

  /* Table */
  .table-wrap { overflow-x: auto; border: 1px solid $line; border-radius: 12px; margin-bottom: 16px; }
  .mcp-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 15px;
    th, td { text-align: left; padding: 13px 18px; vertical-align: top; }
    thead th { background: #f7f9fc; font-weight: 700; color: $ink; border-bottom: 1px solid $line; }
    tbody tr + tr td { border-top: 1px solid $line; }
    td code { white-space: nowrap; }
  }

  /* Security checklist */
  ul.checks { list-style: none; padding-left: 0; margin: 8px 0 12px; }
  ul.checks li {
    position: relative;
    padding-left: 30px;
    margin-bottom: 12px;
    color: #414b5a;
  }
  ul.checks li::before {
    content: "✓";
    position: absolute; left: 0; top: 0;
    width: 20px; height: 20px;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 800;
    color: #0a7d3c; background: #e6f6ec; border-radius: 50%;
  }

  /* Support */
  .support-links { display: flex; align-items: center; gap: 18px; margin-top: 16px; flex-wrap: wrap; }
  .support-links__btn {
    display: inline-block;
    background: $blue; color: #fff;
    padding: 12px 26px; border-radius: 999px;
    font-weight: 600; text-decoration: none;
    transition: background .2s ease;
    &:hover { background: #1a3cae; color: #fff; }
  }
  .support-links__link { color: $blue; font-weight: 600; text-decoration: none; &:hover { text-decoration: underline; } }

  a { color: $blue; }
}

@media only screen and (max-width: 991px) {
  .mcp-doc {
    .mcp-facts { grid-template-columns: 1fr; }
    .cap-grid { grid-template-columns: 1fr; }
    h2 { font-size: 24px; }
  }
}
</style>
